import { BillingFlowEnum, IOrderEntity, TBillingFlow } from "../Services/api/orders/orders.interface";

export const DEFAULT_BILLING_FLOW: TBillingFlow = BillingFlowEnum.SALE;

export const billingFlowLabels: Record<TBillingFlow, string> = {
  [BillingFlowEnum.SALE]: "Venda",
  [BillingFlowEnum.BONUS]: "Bonificação",
  [BillingFlowEnum.REWORK]: "Retrabalho",
  [BillingFlowEnum.SUFRAMA]: "Suframa",
  [BillingFlowEnum.COMMODATUM]: "Comodato",
};

export const billingFlowOptions: { value: TBillingFlow; label: string }[] = [
  { value: BillingFlowEnum.SALE, label: "Venda" },
  { value: BillingFlowEnum.BONUS, label: "Bonificação" },
  { value: BillingFlowEnum.REWORK, label: "Retrabalho" },
  { value: BillingFlowEnum.SUFRAMA, label: "Suframa" },
  { value: BillingFlowEnum.COMMODATUM, label: "Comodato" },
];

export const billingFlowTagColors: Record<TBillingFlow, { backgroundColor: string; textColor: string }> = {
  [BillingFlowEnum.SALE]: { backgroundColor: '#e0e0e0', textColor: '#424242' },
  [BillingFlowEnum.BONUS]: { backgroundColor: '#fff4bf', textColor: '#7a4d00' },
  [BillingFlowEnum.REWORK]: { backgroundColor: '#dbeafe', textColor: '#1d4ed8' },
  [BillingFlowEnum.SUFRAMA]: { backgroundColor: '#dcfce7', textColor: '#166534' },
  [BillingFlowEnum.COMMODATUM]: { backgroundColor: '#f3e8ff', textColor: '#6b21a8' },
};

export const billingFlowSuggestionTagColors = {
  backgroundColor: '#fff3cd',
  textColor: '#7a4d00',
} as const;

const billingFlowAliases: Record<string, TBillingFlow> = {
  VENDA: BillingFlowEnum.SALE,
  SALES: BillingFlowEnum.SALE,
  RETRABALHO: BillingFlowEnum.REWORK,
  BONIFICACAO: BillingFlowEnum.BONUS,
  BONIFICATION: BillingFlowEnum.BONUS,
  COMODATO: BillingFlowEnum.COMMODATUM,
  COMMODATUM: BillingFlowEnum.COMMODATUM,
};

export const normalizeBillingFlow = (billingFlow?: string | null): TBillingFlow => {
  if (!billingFlow) return DEFAULT_BILLING_FLOW;

  const normalizedFlow = billingFlow.trim().toUpperCase();

  if (
    normalizedFlow === BillingFlowEnum.SALE ||
    normalizedFlow === BillingFlowEnum.BONUS ||
    normalizedFlow === BillingFlowEnum.REWORK ||
    normalizedFlow === BillingFlowEnum.SUFRAMA ||
    normalizedFlow === BillingFlowEnum.COMMODATUM
  ) {
    return normalizedFlow as TBillingFlow;
  }

  return billingFlowAliases[normalizedFlow] || DEFAULT_BILLING_FLOW;
};

export const getOrderBillingFlow = (order?: Pick<IOrderEntity, "billing_flow" | "billingFlow">): TBillingFlow => {
  return normalizeBillingFlow(order?.billing_flow || order?.billingFlow);
};

export const getBillingFlowSuggestion = (couponCode?: string | null): TBillingFlow | null => {
  if (!couponCode) return null;

  const normalizedCoupon = couponCode
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

  if (normalizedCoupon.startsWith("RETRA")) return BillingFlowEnum.REWORK;
  if (normalizedCoupon.startsWith("BONIFICA")) return BillingFlowEnum.BONUS;

  return null;
};

const getCouponCodeFromNsOrder = (nsOrder: any): string | null => {
  const coupon = Array.isArray(nsOrder?.coupon) ? nsOrder.coupon[0] : nsOrder?.coupon;
  const coupons = Array.isArray(nsOrder?.coupons) ? nsOrder.coupons[0] : nsOrder?.coupons;

  return coupon?.code || coupons?.code || coupon?.name || coupons?.name || null;
};

export const getOrderCouponCode = (order?: Pick<IOrderEntity, "couponCode" | "nsOrderStringified" | "orderStringified">): string | null => {
  if (order?.couponCode) return order.couponCode;

  const rawNsOrder = order?.nsOrderStringified || order?.orderStringified;
  if (!rawNsOrder) return null;

  try {
    const nsOrder = JSON.parse(rawNsOrder);
    return getCouponCodeFromNsOrder(nsOrder);
  } catch {
    return null;
  }
};

export const getOrderNsNumber = (order?: Pick<IOrderEntity, "nsOrderStringified" | "orderStringified">): string | null => {
  const rawNsOrder = order?.nsOrderStringified || order?.orderStringified;
  if (!rawNsOrder) return null;

  try {
    const nsOrder = JSON.parse(rawNsOrder);
    return nsOrder?.number ? String(nsOrder.number) : null;
  } catch {
    return null;
  }
};

export const getBillingFlowDivergenceMessage = (order?: IOrderEntity): string | null => {
  const couponCode = getOrderCouponCode(order);
  const suggestion = getBillingFlowSuggestion(couponCode);
  const currentFlow = getOrderBillingFlow(order);

  if (!suggestion || currentFlow === suggestion) return null;

  return `Cupom ${couponCode} detectado. Sugestao do Hub: faturar como ${billingFlowLabels[suggestion]}. Fluxo selecionado agora: ${billingFlowLabels[currentFlow]}.`;
};
